import { z } from 'zod';

import {
  BaseFlowBlockSchema,
  BlockType,
  DurationUnits,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';
import { BlockValueTemplateDurationSchema } from '../base/blockValueTemplateDurationSchema.js';
import { FlowVersionTarget } from '../flowExecution.js';
import { FlowArgumentSchema } from '../subFlow/v2/flowArgument.js';

export enum ConversationBlockMode {
  //@deprecated this is Create or Reopen
  WaitUntilClosed = 'WaitUntilClosed',
  //@deprecated this is Reply or Close
  NonBlocking = 'NonBlocking',
  Create = 'Create',
  Reopen = 'Reopen',
  Reply = 'Reply',
  Close = 'Close',
  Update = 'Update',
}

export enum ConversationTagEditMode {
  Edit = 'Edit',
  Replace = 'Replace',
}

const noPatientResponseTimeoutMaxDuration = {
  value: 30,
  units: DurationUnits.Days,
};

export const CreateConversationConfigSchema = z.object({
  agentPhoneNumber: z.string().min(1),
  firstMessage: z.string().min(1),
  tags: z.array(z.string().min(1)).refine((tags) => new Set(tags).size === tags.length, {
    message: 'All items must be unique, no duplicate values allowed',
  }),
  timeout: BlockValueTemplateDurationSchema({
    pathName: 'timeout',
    maxDuration: {
      value: 30,
      units: DurationUnits.Days,
    },
  }),
  noPatientResponseTimeout: BlockValueTemplateDurationSchema({
    pathName: 'noPatientResponseTimeout',
    maxDuration: noPatientResponseTimeoutMaxDuration,
  }).optional(),
  priority: z.string().optional(),
  patientReplyHandler: z
    .object({
      flowDefinitionId: z.string(),
      versionTarget: z.nativeEnum(FlowVersionTarget),
      flowId: z.string(),
      metadata: z.array(FlowArgumentSchema).optional(),
    })
    .optional(),
  mode: z.literal(ConversationBlockMode.Create),
});
export type CreateConversationConfig = z.infer<typeof CreateConversationConfigSchema>;

export const ReopenConversationConfigSchema = z.object({
  existingConversationId: z.string().min(1),
  firstMessage: z.string().optional(),
  timeout: BlockValueTemplateDurationSchema({
    pathName: 'timeout',
    maxDuration: {
      value: 30,
      units: DurationUnits.Days,
    },
  }),
  noPatientResponseTimeout: BlockValueTemplateDurationSchema({
    pathName: 'noPatientResponseTimeout',
    maxDuration: noPatientResponseTimeoutMaxDuration,
  }).optional(),
  mode: z.literal(ConversationBlockMode.Reopen),
});
export type ReopenConversationConfig = z.infer<typeof ReopenConversationConfigSchema>;

export const ReplyConversationConfigSchema = z.object({
  existingConversationId: z.string().min(1),
  firstMessage: z.string().min(1),
  mode: z.literal(ConversationBlockMode.Reply),
});
export type ReplyConversationConfig = z.infer<typeof ReplyConversationConfigSchema>;

export const UpdateConversationConfigSchema = z.object({
  existingConversationId: z.string().min(1),
  mode: z.literal(ConversationBlockMode.Update),
  tagEditMode: z.nativeEnum(ConversationTagEditMode),
  tagsToAdd: z.array(z.string().min(1)).optional().nullable(),
  tagsToRemove: z.array(z.string().min(1)).optional().nullable(),
  tags: z.array(z.string().min(1)).optional().nullable(),
});
export type UpdateConversationConfig = z.infer<typeof UpdateConversationConfigSchema>;

export const CloseConversationConfigSchema = z.object({
  existingConversationId: z.string().min(1),
  firstMessage: z.string().optional(),
  mode: z.literal(ConversationBlockMode.Close),
  resolutionReason: z.string().optional(),
  resolutionDetails: z.record(z.string(), z.unknown()).optional(),
});
export type CloseConversationConfig = z.infer<typeof CloseConversationConfigSchema>;

/**
 * @deprecated use mode create, reopen, reply or close
 */
export const BaseConversationBlockConfigSchemaDeprecated = z.object({
  agentPhoneNumber: z.string(),
  firstMessage: z.string(),
  tags: z.array(z.string().min(1)).refine((tags) => new Set(tags).size === tags.length, {
    message: 'All items must be unique, no duplicate values allowed',
  }),
  timeout: BlockValueTemplateDurationSchema({
    pathName: 'timeout',
    maxDuration: {
      value: 30,
      units: DurationUnits.Days,
    },
  }),
  noPatientResponseTimeout: BlockValueTemplateDurationSchema({
    pathName: 'noPatientResponseTimeout',
    maxDuration: noPatientResponseTimeoutMaxDuration,
  }).optional(),
  priority: z.string().optional(),
  existingConversationId: z.string().optional(),
  patientReplyHandler: z
    .object({
      flowDefinitionId: z.string(),
      versionTarget: z.nativeEnum(FlowVersionTarget),
      flowId: z.string(),
      metadata: z.array(FlowArgumentSchema).optional(),
    })
    .optional(),
});
export const WaitUntilClosedConversationBlockConfigSchema =
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  BaseConversationBlockConfigSchemaDeprecated.extend({
    mode: z
      .literal(ConversationBlockMode.WaitUntilClosed)
      .optional()
      .default(ConversationBlockMode.WaitUntilClosed),
  });

export const NonBlockingConversationBlockConfigSchema =
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  BaseConversationBlockConfigSchemaDeprecated.extend({
    mode: z.literal(ConversationBlockMode.NonBlocking),
  });

export const ConversationBlockConfigSchema = z.discriminatedUnion('mode', [
  CreateConversationConfigSchema,
  ReopenConversationConfigSchema,
  ReplyConversationConfigSchema,
  CloseConversationConfigSchema,
  UpdateConversationConfigSchema,
  WaitUntilClosedConversationBlockConfigSchema,
  NonBlockingConversationBlockConfigSchema,
]);

export const ConversationBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.Conversation),
  config: ConversationBlockConfigSchema,
  returnType: returnTypeSchema(ProducedType.AgentConversation),
});

export const ConversationBlockSchemaDeprecated = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.Conversation),
  config: z.union([
    WaitUntilClosedConversationBlockConfigSchema,
    NonBlockingConversationBlockConfigSchema,
  ]),
  returnType: returnTypeSchema(ProducedType.AgentConversation),
});

export type ConversationBlock = z.infer<typeof ConversationBlockSchema>;
/**
 * @deprecated use ConversationBlockConfigSchema
 */
export type ConversationBlockConfigDeprecated = z.infer<
  | typeof WaitUntilClosedConversationBlockConfigSchema
  | typeof NonBlockingConversationBlockConfigSchema
>;
